import React from "react";
import "./resellerSharing.css";
import { sharedPieceUrl } from "./publicCatalogueHelpers";
import { useAskOnWhatsApp } from "./useAskOnWhatsApp";

type Props = {
  /** The id the server gave this viewer (an alias on client links); only used in the piece link. */
  pieceId: string;
  pieceName: string;
  liked: boolean;
  onToggleLike: () => void;
  /** Reseller's WhatsApp number (digits with country code). */
  whatsApp?: string | null;
  curatorName?: string | null;
  pageUrl?: string;
  variant?: "card" | "detail";
  /** The share link the client is on, for the short link to this piece. */
  catalogueId?: string;
  linkToken?: string;
  sessionToken?: string;
};

/** A reseller's client: like a piece, or ask the reseller about it. No cart, no studio. */
const ClientPieceActions: React.FC<Props> = ({
  pieceId,
  pieceName,
  liked,
  onToggleLike,
  whatsApp,
  curatorName,
  pageUrl,
  variant = "card",
  catalogueId,
  linkToken,
  sessionToken,
}) => {
  const ask = useAskOnWhatsApp({
    phone: whatsApp,
    pieceName,
    ctx: { catalogueId, linkToken, sessionToken, pieceId },
    fallback: { pieceUrl: pageUrl ?? currentPieceUrl(pieceId) },
    // A piece page asks for its one short link up front; a grid card waits for a hover or touch.
    eager: variant === "detail" && Boolean(whatsApp),
  });
  const askLabel = variant === "detail" && curatorName ? `Ask ${firstName(curatorName)} on WhatsApp` : "Ask on WhatsApp";
  // On cards the heart overlays the photo (positioned against the card), the row keeps Ask.
  return (
    <div className={variant === "detail" ? "hkp-client-detail-actions" : "hkp-client-actions"}>
      <button
        type="button"
        className={`hkp-client-like${variant === "card" ? " is-overlay" : ""}`}
        aria-pressed={liked}
        aria-label={liked ? `Unlike ${pieceName}` : `Like ${pieceName}`}
        onClick={onToggleLike}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path
            d="M12 20.3s-7.3-4.4-9.2-9.1C1.5 8 3.4 4.7 6.7 4.7c2 0 3.4 1.1 4.2 2.4h2.2c.8-1.3 2.2-2.4 4.2-2.4 3.3 0 5.2 3.3 3.9 6.5-1.9 4.7-9.2 9.1-9.2 9.1z"
            fill={liked ? "currentColor" : "none"}
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {whatsApp ? (
        <a
          className="hkp-client-ask"
          href={ask.href}
          onClick={ask.onClick}
          onPointerEnter={ask.warm}
          onTouchStart={ask.warm}
          onFocus={ask.warm}
          target="_blank"
          rel="noopener noreferrer"
        >
          {askLabel}
        </a>
      ) : null}
    </div>
  );
};

/** This piece on the page the client is on, so the reseller sees exactly which piece. */
function currentPieceUrl(pieceId: string): string | undefined {
  return typeof window !== "undefined" ? sharedPieceUrl(window.location.href, pieceId) || undefined : undefined;
}

function firstName(name: string): string {
  const first = name.trim().split(/\s+/)[0] || name;
  return first.length > 16 ? `${first.slice(0, 15)}…` : first;
}

export default ClientPieceActions;
